import * as z from "zod";
import {
  completedChaptersViolation,
  ProofRecordingChapters,
} from "@repo/codesmith-common/src/schema/proof_recording_chapters";
import { ProofRecordingCut } from "@repo/codesmith-common/src/schema/proof_recording_cut";
import {
  ProofApplicationSummary,
  ProofLifecycleStage,
  ProofLifecycleStatus,
  ProofSetupRemediation,
  ProofSetupReason,
  ProofSetupServiceState,
} from "@repo/codesmith-common/src/schema/proof_lifecycle";
import { formatDurationMs } from "@/lib/utils";
import { claimDiffers } from "./proofClaimLabel";

const ProofClaimStatus = z.enum([
  "pending",
  "running",
  "observed",
  "blocked",
  "skipped",
  "not_run",
]);
export type ProofClaimStatus = z.infer<typeof ProofClaimStatus>;

const ProofClaimReason = z.enum([
  "credentials",
  "network",
  "environment",
  "flaky",
  "other",
]);
export type ProofClaimReason = z.infer<typeof ProofClaimReason>;

/**
 * Keeps valid speedups when camera rectangles cannot be placed in this recording. A cut with
 * neither speedups nor zooms (post-processing stores one whenever the camera stage runs) plays
 * as the recording itself, so it is no cut: no pill, no footage link, the whole recording's rate.
 */
function cutForRecording(
  cut: ProofRecordingCut,
  video: { duration_ms: number; width?: number; height?: number },
): ProofRecordingCut | undefined {
  const { width, height } = video;
  if (!cut.speedups.every((range) => range.end_ms <= video.duration_ms)) {
    return undefined;
  }
  const zoomsFit =
    width !== undefined &&
    height !== undefined &&
    cut.zooms.every(
      (zoom) =>
        zoom.end_ms <= video.duration_ms &&
        zoom.x + zoom.w <= width &&
        zoom.y + zoom.h <= height,
    );
  const fitted = zoomsFit ? cut : { ...cut, zooms: [] };
  return fitted.speedups.length === 0 && fitted.zooms.length === 0
    ? undefined
    : fitted;
}

const RecoverableProofRecordingCut = z
  .object({
    speedups: z.unknown(),
    zooms: z.unknown().optional(),
  })
  .transform(({ speedups, zooms }) => {
    const parsedSpeedups = ProofRecordingCut.safeParse({
      speedups,
      zooms: [],
    });
    if (!parsedSpeedups.success) return undefined;
    const parsedZooms = ProofRecordingCut.safeParse({
      speedups: [],
      zooms: zooms ?? [],
    });
    return {
      speedups: parsedSpeedups.data.speedups,
      zooms: parsedZooms.success ? parsedZooms.data.zooms : [],
    };
  })
  .catch(undefined);

const KnownProofEvidence = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("image"),
    url: z.string(),
    label: z.string().nullable().optional(),
    /** Half of a before/after pair: the merge-base or the PR head. */
    side: z.enum(["base", "head"]).nullable().optional(),
  }),
  z.object({ kind: z.literal("link"), url: z.string(), label: z.string() }),
  z
    .object({
      kind: z.literal("video"),
      recording_id: z.uuid(),
      url: z.string(),
      label: z.string().nullable().optional(),
      poster_url: z.string(),
      gif_url: z.string().optional(),
      duration_ms: z.number().int().positive().max(2_147_483_647),
      /** Frame size in pixels, so the player opens at the recording's aspect ratio before any media loads. */
      width: z.number().int().positive().optional(),
      height: z.number().int().positive().optional(),
      /** The acting agent's groups, closed and clipped to the recording when it stopped; absent on rows stored before chapters, which play as one plain interval. */
      chapters: ProofRecordingChapters.optional().catch(undefined),
      /** The trim post-processing wrote seconds after the claim closed; absent until then, and on rows stored before cuts. */
      cut: RecoverableProofRecordingCut.optional(),
      /** The encoder exited before the agent stopped (its 1 h / 3.5 GiB safety stop, a crash, or a full disk), so the clip ends before the proof did. */
      recording_truncated: z.literal(true).optional(),
      action_detail_incomplete: z.literal(true).optional(),
    })
    // Chapters or a cut that break the recording's rules cost only themselves, not the report: the clip plays as one plain interval at full length.
    .overwrite(({ chapters, cut, ...video }) => {
      const recordingCut = cut && cutForRecording(cut, video);
      return {
        ...video,
        ...(chapters && !completedChaptersViolation(chapters, video)
          ? { chapters }
          : {}),
        ...(recordingCut ? { cut: recordingCut } : {}),
      };
    }),
]);

const KNOWN_EVIDENCE_KINDS: ReadonlySet<string> = new Set(
  KnownProofEvidence.options.map((option) => option.shape.kind.value),
);

// A kind this build does not know, from a newer backend, keeps the snapshot
// parsing and shows as a link to its URL; a malformed known kind still fails.
const UnknownProofEvidence = z
  .object({
    kind: z.string().refine((kind) => !KNOWN_EVIDENCE_KINDS.has(kind)),
    url: z.string(),
    label: z.string().nullable().optional(),
  })
  .transform(({ kind, url, label }) => ({
    kind: "link" as const,
    url,
    label: label || kind,
  }));

const ProofEvidence = z.union([KnownProofEvidence, UnknownProofEvidence]);
export type ProofEvidence = z.infer<typeof KnownProofEvidence>;
export type ProofVideoEvidence = Extract<ProofEvidence, { kind: "video" }>;

// head_sha is optional so reports settled before runs tracked heads still parse.
export const ProofClaimAttempt = z.object({
  status: ProofClaimStatus,
  head_sha: z.string().nullable(),
  observed: z.string().nullable(),
  reason: ProofClaimReason.nullable(),
  evidence: z.array(ProofEvidence),
  started_at: z.string().nullable(),
  closed_at: z.string().nullable(),
  matches_claim: z.boolean().nullable().optional(),
  carried: z.boolean().optional(),
  claim: z.string().nullable().optional(),
});
export type ProofClaimAttempt = z.infer<typeof ProofClaimAttempt>;

export const ProofClaim = z.object({
  id: z.string(),
  title: z.string(),
  claim: z.string(),
  hero: z.boolean(),
  status: ProofClaimStatus,
  observed: z.string().nullable(),
  reason: ProofClaimReason.nullable(),
  evidence: z.array(ProofEvidence),
  started_at: z.string().nullable(),
  closed_at: z.string().nullable(),
  head_sha: z.string().nullable().optional(),
  attempts: z.array(ProofClaimAttempt).optional(),
  /** Observed claims only: whether what Proof saw matched the statement. */
  matches_claim: z.boolean().nullable().optional(),
  /** Closed in a reused child's earlier run and not re-checked in this one. */
  carried: z.boolean().optional(),
  /** The head a push's diff left this passing result untouched on; it counts there while that is the run's head. */
  carried_onto: z.string().nullable().optional(),
  // Optional so cases planned before Proof tagged them still parse.
  surface: z.enum(["frontend", "backend"]).nullable().optional(),
  edge_case: z.boolean().optional(),
  /** Blocked-on-credentials claims only: the env var keys the sandbox lacked. */
  missing_env_vars: z.array(z.string()).nullable().optional(),
});
export type ProofClaim = z.infer<typeof ProofClaim>;

export const ProofFinding = z.object({
  title: z.string(),
  severity: z.enum(["issue", "note"]),
  observed: z.string(),
  evidence: z.array(ProofEvidence),
  head_sha: z.string().nullable().optional(),
  /** Reported by a reused child's earlier run; it counts again only once this run reports it. */
  carried: z.boolean().optional(),
});
export type ProofFinding = z.infer<typeof ProofFinding>;

export { ProofSetupReason, ProofSetupServiceState };

/** One service the run's claims went through, as `report_setup` recorded it (App\Data\Proof\ProofSetupService). */
export const ProofSetupService = z
  .object({
    /** Agent-chosen and stable across runs: `"dashboard"`, `"postgres"`. */
    id: z.string(),
    name: z.string(),
    state: ProofSetupServiceState,
    /** Why it is not live; null on a live row. */
    reason: ProofSetupReason.nullable(),
    /** `missing_key` only: the keys that would make it live. */
    missing_env_vars: z.array(z.string()),
    /** `configuration` only: the setting as the repository names it. */
    setting: z.string().nullable(),
    /** The child's one line on the cause, or what makes the stand-in enough; absent with `missing_key`, the clause with `other`. */
    because: z.string().nullable(),
    /** Ids of the claims that go through it; empty only with `not_exercised`. */
    claims: z.array(z.string()),
    command: z.string().nullable(),
    port: z.number().int().nullable(),
    detail: z.string().nullable(),
    reported_at: z.string(),
    remediation: z.unknown().optional(),
  })
  .transform((service) => {
    const parsed = ProofSetupRemediation.safeParse(service.remediation);
    const remediation = parsed.success ? parsed.data : null;
    const matches =
      service.state !== "live" &&
      (remediation?.kind === "request_env_vars"
        ? service.reason === "missing_key" &&
          remediation.keys.length === service.missing_env_vars.length &&
          remediation.keys.every(
            (key, index) => key === service.missing_env_vars[index],
          )
        : remediation?.kind === "retry_proof"
          ? service.reason === "time_budget" &&
            remediation.cause === "time_budget"
          : remediation === null);
    return { ...service, remediation: matches ? remediation : null };
  });
export type ProofSetupService = z.infer<typeof ProofSetupService>;

/** One of a stage's longest items. */
export const ProofLifecycleCall = z.object({
  label: z.string(),
  ms: z.number().int(),
  failed: z.boolean(),
});
export type ProofLifecycleCall = z.infer<typeof ProofLifecycleCall>;

/** A failed call no later call in its stage answered. */
export const ProofLifecycleFailure = z.object({
  label: z.string(),
  /** `not_ready_econnrefused`, `port_in_use`, `oom_or_137`, … */
  signature: z.string().nullable(),
  ms: z.number().int(),
});
export type ProofLifecycleFailure = z.infer<typeof ProofLifecycleFailure>;

/** One stage of the run (App\Codesmith\Proof\ProofRunLifecycle). */
export const ProofLifecycleStageSummary = z.object({
  stage: ProofLifecycleStage,
  status: ProofLifecycleStatus,
  /** Offsets from the run's `started_at`; a stage still open on an unsettled run has no end. */
  start_ms: z.number().int(),
  end_ms: z.number().int().nullable(),
  /** Wall clock the stage's work covered, overlapping work counted once. */
  ms: z.number().int(),
  calls: z.number().int(),
  failures: z.array(ProofLifecycleFailure),
  detail: z.array(ProofLifecycleCall),
  /** The `report_setup` rows; only the `services` stage has any. */
  services: z.array(ProofSetupService),
  app: ProofApplicationSummary.optional(),
});
export type ProofLifecycleStageSummary = z.infer<
  typeof ProofLifecycleStageSummary
>;

/** The child's account of the run by section, each in Markdown (App\Data\Proof\ProofRunNote). */
export const ProofRunNote = z.object({
  description: z.string(),
  result: z.string().nullable(),
  setup: z.string().nullable(),
  not_tested: z.array(z.string()),
});
export type ProofRunNote = z.infer<typeof ProofRunNote>;

/** An earlier run of the same child, as this run keeps it: its head, its closing words and its stages. */
export const ProofLifecycleHistoryEntry = z.object({
  run_id: z.string(),
  head_sha: z.string().nullable(),
  note: z.string().nullable(),
  ready_in_ms: z.number().int().nullable(),
  stages: z.array(ProofLifecycleStageSummary),
});
export type ProofLifecycleHistoryEntry = z.infer<
  typeof ProofLifecycleHistoryEntry
>;

export const ProofLifecycle = z.object({
  set_up_on_sha: z.string().nullable(),
  /** Null while booting. */
  ready_in_ms: z.number().int().nullable(),
  /** `fresh` booted the environment in this run; `carried` took over a reused child's. */
  reuse: z.enum(["fresh", "carried"]),
  /** Service ids this run restarted (carried runs). */
  restarted: z.array(z.string()),
  /** The runs before this one on a reused child, origin first; empty on a fresh run. */
  history: z.array(ProofLifecycleHistoryEntry),
  /** In the order the stages began. */
  stages: z.array(ProofLifecycleStageSummary),
});
export type ProofLifecycle = z.infer<typeof ProofLifecycle>;

/** The backend's ProofRunSnapshot on the wire: live frames, `active_proof_runs`, and the settled `proof_report`. */
export const ProofRunSnapshot = z.object({
  child_session_id: z.string(),
  // A reused child holds one run per follow-up; optional for snapshots sent before runs had ids.
  run_id: z.string().optional(),
  pr_url: z.string(),
  pr_number: z.number(),
  // Optional so runs spawned before the backend sent titles still parse.
  pr_title: z.string().nullable().optional(),
  repository: z
    .string()
    .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/)
    .optional(),
  repository_model_id: z.number().int().positive().nullable().optional(),
  frame_revision: z.string().nullable().optional(),
  status: z.enum(["planning", "running", "finished", "aborted"]),
  aborted_reason: z.string().nullable(),
  /** The settled child's own closing words; optional so live frames and
   * older settled reports still parse. */
  reply: z.string().nullable().optional(),
  /** Null until the child reports it, and for orgs without the stage rows; optional so older reports parse. */
  run_note: ProofRunNote.nullable().optional(),
  head_sha: z.string().nullable().optional(),
  /** A pushed head the run has not checked yet. */
  pending_head_sha: z.string().nullable().optional(),
  /** A reused child's run opened on a change (a new head, an edited description) and must `replan` before it adds or starts a claim. */
  ledger_owed: z.boolean().nullable().optional(),
  /** A `replan` landed and no claim has started since. */
  plan_reopened: z.boolean().nullable().optional(),
  // Optional so older settled reports without run timestamps still parse.
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
  setup_started_at: z.string().nullable().optional(),
  cases: z.array(ProofClaim),
  findings: z.array(ProofFinding),
  /** Null until a stage has a clock, and for orgs without the stage rows; optional so older reports parse. */
  lifecycle: ProofLifecycle.nullable().optional(),
});
export type ProofRunSnapshot = z.infer<typeof ProofRunSnapshot>;
/** A run as the report reads it, without the child it belongs to: a wire frame, or a shared report. */
export type ProofRunReport = Omit<ProofRunSnapshot, "child_session_id">;

/** `ses_1:run_2`; runs sent before run ids fall back to their timestamps. */
export function proofRunKey(run: ProofRunSnapshot): string {
  const runPart = run.run_id ?? run.started_at ?? run.finished_at ?? "";
  return `${run.child_session_id}:${runPart}`;
}

export interface ProofPanelClaim {
  id: string;
  title: string;
  claim: string;
  hero: boolean;
  status: ProofClaimStatus;
  observed?: string;
  reason?: ProofClaimReason;
  /** Observed claims only; unset when the child did not say. */
  matchesClaim?: boolean;
  evidence: ProofEvidence[];
  duration?: string;
  /** ISO timestamps bounding the case's work, for the Steps chart. */
  startedAt?: string;
  closedAt?: string;
  /** Short SHA of the older head this claim closed on; it is not evidence for the run's head unless `carriedOnto`. */
  olderHead?: string;
  /** Passed on `olderHead`, and no push since touches its files, so it counts for the run's head. */
  carriedOnto?: boolean;
  /** Closed by a reused child's earlier run and not re-checked in this one; its star says so. */
  earlierRun?: boolean;
  attempts?: ProofPanelAttempt[];
  surface?: "frontend" | "backend";
  edgeCase?: boolean;
  /** Env var keys a blocked (credentials) claim named as missing from the sandbox. */
  missingEnvVars?: string[];
}

export interface ProofPanelAttempt {
  status: ProofClaimStatus;
  observed?: string;
  reason?: ProofClaimReason;
  evidence: ProofEvidence[];
  duration?: string;
  olderHead?: string;
  claim?: string;
}

export type ProofPanelFinding = Omit<ProofFinding, "carried"> & {
  olderHead?: string;
  /** Reported by a reused child's earlier run; shown with its star until this run reports it again. */
  earlierRun?: boolean;
};

/** The run's claims and findings as typed data; `deriveRunView` reads the frame for what to show. */
export type ProofPanelRun =
  | {
      phase: "starting" | "planning" | "running";
      cases: ProofPanelClaim[];
      findings: ProofPanelFinding[];
    }
  | {
      phase: "finished";
      cases: ProofPanelClaim[];
      findings: ProofPanelFinding[];
      /** Set when the run ended before the child reported, e.g. `interrupted`. */
      abortedReason?: string;
      /** The child's closing words as one Markdown note. */
      summary?: string;
      /** The child's account by section, where it reported one. */
      runNote?: ProofRunNote;
      duration?: string;
    };

export function isOpenClaim(item: Pick<ProofPanelClaim, "status">): boolean {
  return item.status === "pending" || item.status === "running";
}

export const shortSha = (sha: string) => sha.slice(0, 8);

/** Observed on the run's head, or carried onto it from an older one. */
export function observedOnRunHead(
  item: Pick<ProofPanelClaim, "status" | "olderHead" | "carriedOnto">,
): boolean {
  return item.status === "observed" && (!item.olderHead || !!item.carriedOnto);
}

/** `"9f8e7d6c"` when `sha` is known and is not the head the run is proving. */
export function olderHead(
  sha: string | null | undefined,
  runHead: string | null | undefined,
): string | undefined {
  return sha && runHead && sha !== runHead ? shortSha(sha) : undefined;
}

/** `"3m 14s"` when both ISO timestamps parse. */
export function durationBetween(
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
): string | undefined {
  const started = startedAt ? Date.parse(startedAt) : NaN;
  const ended = endedAt ? Date.parse(endedAt) : NaN;
  return Number.isFinite(started) && Number.isFinite(ended)
    ? formatDurationMs(ended - started)
    : undefined;
}

export function panelClaim(
  item: ProofClaim,
  runHead: string | null | undefined,
): ProofPanelClaim {
  const carriedOnto = !!runHead && item.carried_onto === runHead;
  return {
    id: item.id,
    title: item.title,
    claim: item.claim,
    hero: item.hero,
    status: item.status,
    observed: item.observed ?? undefined,
    reason: item.reason ?? undefined,
    matchesClaim: item.matches_claim ?? undefined,
    evidence: item.evidence,
    duration: durationBetween(item.started_at, item.closed_at),
    startedAt: item.started_at ?? undefined,
    closedAt: item.closed_at ?? undefined,
    olderHead: isOpenClaim(item)
      ? undefined
      : olderHead(item.head_sha, runHead),
    carriedOnto: carriedOnto || undefined,
    // A carried claim the earlier run never ran comes back `pending` and has no result to star.
    earlierRun:
      (item.carried && !carriedOnto && !isOpenClaim(item)) || undefined,
    attempts: (item.attempts ?? []).map((attempt) => ({
      status: attempt.status,
      observed: attempt.observed ?? undefined,
      reason: attempt.reason ?? undefined,
      evidence: attempt.evidence,
      duration: durationBetween(attempt.started_at, attempt.closed_at),
      olderHead: olderHead(attempt.head_sha, runHead),
      claim: attempt.claim ?? undefined,
    })),
    surface: item.surface ?? undefined,
    edgeCase: item.edge_case || undefined,
    missingEnvVars: item.missing_env_vars?.length
      ? item.missing_env_vars
      : undefined,
  };
}

/** Mirrors `CodesmithProofRun::leftFailingClaimBehind`: such a claim keeps the check amber whoever it is, so the card keeps showing it. */
function leftBehindUnresolved(
  item: ProofClaim,
  runHead: string | null | undefined,
): boolean {
  if (!!runHead && item.carried_onto === runHead) return false;
  return (
    item.status === "blocked" ||
    item.status === "not_run" ||
    claimDiffers({
      status: item.status,
      matchesClaim: item.matches_claim ?? undefined,
    })
  );
}

/**
 * A reused child's earlier results stay, as GitHub's report lists them, while
 * the run re-proves them and once it finishes. An aborted run has no report,
 * so they leave, unless carried onto this run's head or unresolved.
 */
export function carriedClaimKept(
  item: ProofClaim,
  runHead: string | null | undefined,
  aborted: boolean,
): boolean {
  return (
    !item.carried ||
    !aborted ||
    (!!runHead && item.carried_onto === runHead) ||
    leftBehindUnresolved(item, runHead)
  );
}

export function carriedFindingKept(
  finding: ProofFinding,
  aborted: boolean,
): boolean {
  return !finding.carried || !aborted;
}

/**
 * The frame has a report to show (claims this run keeps, or findings) rather
 * than the viewfinder. On the data model so the workspace's tab and artifact
 * code, in every page's shared chunk, need not load the pane's view derivation.
 */
export function hasProofBody(run: ProofRunReport): boolean {
  const { phase, findings } = panelRunFromSnapshot(run);
  return phase !== "starting" || findings.length > 0;
}

export function panelRunFromSnapshot(run: ProofRunReport): ProofPanelRun {
  const aborted = run.status === "aborted";
  const cases = run.cases
    .filter((item) => carriedClaimKept(item, run.head_sha, aborted))
    .map((item) => panelClaim(item, run.head_sha));
  const findings = run.findings
    .filter((finding) => carriedFindingKept(finding, aborted))
    .map(({ carried, ...finding }) => ({
      ...finding,
      olderHead: olderHead(finding.head_sha, run.head_sha),
      earlierRun: carried || undefined,
    }));
  if (run.status === "finished" || aborted) {
    return {
      phase: "finished",
      cases,
      findings,
      abortedReason: run.aborted_reason ?? undefined,
      summary: run.reply?.trim() || undefined,
      runNote: run.run_note ?? undefined,
      duration: durationBetween(run.started_at, run.finished_at),
    };
  }
  if (run.status === "running") return { phase: "running", cases, findings };
  return {
    phase: cases.length === 0 ? "starting" : "planning",
    cases,
    findings,
  };
}
